import { remainingWeekdaySessions } from '../../services/tradingSessions';
import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import { calculateOptionValueFromAPI } from '../../services/optionPricing';
import './StockAnalysis.css';
import { datedQuoteContext } from '../../services/quoteDates';

const sourceTime = value => Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString('fa-IR', {timeZone:'Asia/Tehran', dateStyle:'medium', timeStyle:'medium'}) : 'نامشخص';
const sourceDay = value => value && Number.isFinite(Date.parse(value)) ? new Date(`${value}T12:00:00+03:30`).toLocaleDateString('fa-IR', {timeZone:'Asia/Tehran'}) : 'تأیید نشده';

function StockAnalysis({ settings, preselectedSymbol, onPreselectedUsed }) {
  const [transactions, setTransactions] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [allSymbols, setAllSymbols] = useState([]);
  const [activeSymbols, setActiveSymbols] = useState([]);
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const selectedSymbolRef = useRef(selectedSymbol);
  selectedSymbolRef.current = selectedSymbol;
  const onPreselectedUsedRef = useRef(onPreselectedUsed);
  onPreselectedUsedRef.current = onPreselectedUsed;
  const [currentPrice, setCurrentPrice] = useState(0);
  const [lastUpdate, setLastUpdate] = useState('');
  const [viewFilter, setViewFilter] = useState('🟢 باز');
  const [showOnlyPositive, setShowOnlyPositive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // حالت مودال فروش
  const [showSellModal, setShowSellModal] = useState(false);
  const [selectedStepForSell, setSelectedStepForSell] = useState(null);
  const [sellPrice, setSellPrice] = useState(0);
  const [sellQuantity, setSellQuantity] = useState(0);
  const [sellLoading, setSellLoading] = useState(false);
  
  // حالت فروش گروهی
  const [selectedSteps, setSelectedSteps] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkSellPrice, setBulkSellPrice] = useState(0);
  const [bulkSellDate, setBulkSellDate] = useState(new Date().toISOString().split('T')[0]);
  const [optionContractSize, setOptionContractSize] = useState(1000);


  // داده‌های آپشن و ارزش ذاتی
  const [optionData, setOptionData] = useState(null);
  const [intrinsicValue, setIntrinsicValue] = useState(0);
  const [optionGreeks, setOptionGreeks] = useState(null);
  const [underlyingPrice, setUnderlyingPrice] = useState(0);
  const [previousPremium, setPreviousPremium] = useState(0);
  const previousPremiumEdited = useRef(false);
  const quoteSequence = useRef(0);
  const [previousObservationDate, setPreviousObservationDate] = useState('');
  const [optionRate, setOptionRate] = useState(30);
  const [optionInputs, setOptionInputs] = useState(null);
  const [isOptionSectionOpen, setIsOptionSectionOpen] = useState(false);

  // حالت تسویه فیزیکی فروش فزاینده
  const [showPhysicalModal, setShowPhysicalModal] = useState(false);
  const [selectedPhysicalStep, setSelectedPhysicalStep] = useState(null);
  const [strikePrice, setStrikePrice] = useState(0);
  const [contractSizeVal, setContractSizeVal] = useState(1000);
  const [customSettleAmount, setCustomSettleAmount] = useState(0);
  const [physicalType, setPhysicalType] = useState('put'); // put = اختیار فروش، call = اختیار خرید
  const [baseSymbol, setBaseSymbol] = useState(''); // سهم پایه مثلاً شستا

  // حالت تسویه نقدی (symbol-level)
  const [showCashSettleModal, setShowCashSettleModal] = useState(false);
  const [cashSettleContracts, setCashSettleContracts] = useState(0);
  const [cashSettleFinalPrice, setCashSettleFinalPrice] = useState(0);
  const [cashSettleStrikePrice, setCashSettleStrikePrice] = useState(0);
  const [cashSettleContractSize, setCashSettleContractSize] = useState(1000);
  const [cashSettleSelectAll, setCashSettleSelectAll] = useState(false);

  // حالت تسویه فیزیکی (symbol-level)
  const [showPhysicalSettleModal, setShowPhysicalSettleModal] = useState(false);
  const [physicalSettleContracts, setPhysicalSettleContracts] = useState(0);
  const [physicalSettleFinalPrice, setPhysicalSettleFinalPrice] = useState(0);
  const [physicalSettleStrikePrice, setPhysicalSettleStrikePrice] = useState(0);
  const [physicalSettleContractSize, setPhysicalSettleContractSize] = useState(1000);
  const [physicalSettleSelectAll, setPhysicalSettleSelectAll] = useState(false);

  const openPhysicalSettleModal = (row) => {
    setSelectedPhysicalStep(row);
    setContractSizeVal(1000);
    // تشخیص نوع آپشن بر اساس حرف اول نماد (ط = Put, ض = Call)
    const isPut = selectedSymbol.startsWith('ط');
    setPhysicalType(isPut ? 'put' : 'call');
    
    // تشخیص نماد پایه با حذف اعداد و تبدیل‌های رایج
    const cleanName = selectedSymbol.replace(/[0-9\-]/g, '').substring(1); 
    const baseMap = {
      'ستا': 'شستا',
      'خود': 'خودرو',
      'سپا': 'خساپا',
      'صاد': 'وبصادر',
      'فلا': 'فولاد',
      'ملی': 'فملی',
      'هرم': 'اهرم'
    };
    const derivedBase = baseMap[cleanName] || cleanName;
    setBaseSymbol(derivedBase);
    
    setStrikePrice(0);
    setCustomSettleAmount(0);
    setShowPhysicalModal(true);
  };

  useEffect(() => {
    if (selectedPhysicalStep && strikePrice > 0) {
      const blocked = parseFloat(selectedPhysicalStep.invested) || 0;
      const qty = selectedPhysicalStep.remainingQty;
      
      // محاسبه صحیح: تعداد موقعیت باز * قیمت اعمال - وجه تضمین اولیه
      if (physicalType === 'put') {
        const calculated = (qty * strikePrice) - blocked;
        setCustomSettleAmount(calculated);
      } else {
        setCustomSettleAmount(qty);
      }
    }
  }, [strikePrice, selectedPhysicalStep, physicalType]);


  const handlePhysicalSettleSubmit = async () => {
    if (strikePrice <= 0) {
      alert('لطفاً قیمت اعمال را وارد کنید');
      return;
    }

    try {
      setSellLoading(true);

            // ۱. تسویه و بستن موقعیت آپشن باز (ثبت تراکنش فروش برای کسر و بستن پله شورت‌سل)
      const closeOptionTx = {
        Date: new Date().toISOString().split('T')[0],
        Symbol: selectedSymbol,
        Type: 'sell',
        MarketType: 'Option',
        Quantity: selectedPhysicalStep.remainingQty,
        Price: 0,
        Step: selectedPhysicalStep.step,
        BlockedAmount: 0,
        Note: `تسویه فیزیکی پله ${selectedPhysicalStep.step}`
      };


      await api.post('/api/transactions', closeOptionTx);

            // ۲. ایجاد یا کسر سهم پایه از پرتفوی (مثلا شستا)
      const qtyOfBaseShares = selectedPhysicalStep.remainingQty;

      if (physicalType === 'put') {
        // در اختیار فروش (Put) سهم پایه وارد سبد می‌شود
        const baseStockTx = {
          Date: new Date().toISOString().split('T')[0],
          Symbol: baseSymbol,
          Type: 'buy',
          MarketType: 'Bourse',
          Quantity: qtyOfBaseShares,
          Price: strikePrice,
          Step: `تسویه فیزیکی ${selectedSymbol}`,
          BlockedAmount: 0,
          Note: `تسویه فیزیکی آپشن ${selectedSymbol}`
        };

        await api.post('/api/transactions', baseStockTx);
            } else {
        // در اختیار خرید (Call) سهم پایه از سبد کسر می‌شود (از پایین به بالا)
        // دریافت پله‌های باز سهم پایه
        const { rows: openBaseSteps } = calculateStocksLogic(baseSymbol, 0, '🟢 باز');
        let remainingToDeduct = qtyOfBaseShares;

        // معکوس کردن لیست برای اینکه کسر کردن از آخرین پله (پایین) به سمت اولین پله (بالا) انجام شود
        const reversedSteps = [...openBaseSteps].reverse();

        for (const row of reversedSteps) {
          if (remainingToDeduct <= 0) break;


          // مقداری که از این پله می‌توانیم کسر کنیم
          const deductQty = Math.min(row.remainingQty, remainingToDeduct);
          
          const sellBaseTx = {
            Date: new Date().toISOString().split('T')[0],
            Symbol: baseSymbol,
            Type: 'sell',
            MarketType: 'Bourse',
            Quantity: deductQty,
            Price: strikePrice,
            Step: row.step, // کسر دقیقاً از همین پله
            BlockedAmount: 0,
            Note: `تسویه فیزیکی آپشن ${selectedSymbol}`
          };

          await api.post('/api/transactions', sellBaseTx);

          remainingToDeduct -= deductQty;
        }

        // اگر موجودی پله‌های باز کافی نبود، الباقی را به صورت یک پله کلی ثبت می‌کنیم تا آمار بهم نریزد
        if (remainingToDeduct > 0) {
          const extraTx = {
            Date: new Date().toISOString().split('T')[0],
            Symbol: baseSymbol,
            Type: 'sell',
            MarketType: 'Bourse',
            Quantity: remainingToDeduct,
            Price: strikePrice,
            Step: `تسویه کسری ${selectedSymbol}`,
            BlockedAmount: 0,
            Note: `تسویه فیزیکی آپشن ${selectedSymbol} (کسری موجودی)`
          };
          await api.post('/api/transactions', extraTx);
        }
      }


      alert('✅ تسویه فیزیکی با موفقیت انجام شد و تغییرات سهم پایه در سبد اعمال گردید.');
      setShowPhysicalModal(false);
      await fetchTransactions();
    } catch (error) {
      console.error(error);
      alert('خطا در اعمال تسویه فیزیکی');
    } finally {
      setSellLoading(false);
    }
  };

  
  // ============== توابع کمکی تسویه نقدی / فیزیکی (symbol-level) ==============

  const getSettingValue = (key, defaultValue = 0) => {
    const found = settings.find(s => s.key === key);
    return found ? parseFloat(found.value) : defaultValue;
  };

  const getBaseSymbolForOption = (symbol) => {
    if (!symbol) return '';
    const cleanName = symbol.replace(/[0-9\-]/g, '').substring(1);
    const baseMap = {
      'ستا': 'شستا',
      'خود': 'خودرو',
      'سپا': 'خساپا',
      'صاد': 'وبصادر',
      'فلا': 'فولاد',
      'ملی': 'فملی',
      'هرم': 'اهرم'
    };
    return baseMap[cleanName] || cleanName;
  };

  const getOpenOptionStats = () => {
    if (!resultData) {
      return { totalShares: 0, totalInvested: 0, avgCostPerShare: 0, totalOppCost: 0, rows: [] };
    }
    const openRows = resultData.rows.filter(r => r.status === '🟢 باز');
    const totalShares = openRows.reduce((sum, r) => sum + r.remainingQty, 0);
    const totalInvested = openRows.reduce((sum, r) => sum + r.invested, 0);
    const totalOppCost = openRows.reduce((sum, r) => sum + r.oppCost, 0);
    const avgCostPerShare = totalShares > 0 ? totalInvested / totalShares : 0;
    return { totalShares, totalInvested, avgCostPerShare, totalOppCost, rows: openRows };
  };

  const distributeSellQuantityAcrossSteps = (quantity, rows) => {
    const sells = [];
    let remaining = quantity;
    for (const row of rows) {
      if (remaining <= 0) break;
      const qty = Math.min(row.remainingQty, remaining);
      sells.push({ step: row.step, qty, marketType: row.marketType });
      remaining -= qty;
    }
    return sells;
  };

  const openCashSettleModal = () => {
    const stats = getOpenOptionStats();
    const contracts = Math.floor(stats.totalShares / 1000) || 0;
    setCashSettleContracts(contracts);
    setCashSettleContractSize(1000);
    setCashSettleFinalPrice(0);
    setCashSettleStrikePrice(optionData?.strikePrice || 0);
    setCashSettleSelectAll(true);
    setShowCashSettleModal(true);
  };

  const openPhysicalSettleModalSymbolLevel = () => {
    const stats = getOpenOptionStats();
    const contracts = Math.floor(stats.totalShares / 1000) || 0;
    setPhysicalSettleContracts(contracts);
    setPhysicalSettleContractSize(1000);
    setPhysicalSettleFinalPrice(0);
    setPhysicalSettleStrikePrice(optionData?.strikePrice || 0);
    setPhysicalSettleSelectAll(true);
    setShowPhysicalSettleModal(true);
  };

  const handleCashSettleSubmit = async () => {
    if (cashSettleContracts <= 0) {
      alert('تعداد قرارداد باید بیشتر از صفر باشد');
      return;
    }
    if (cashSettleFinalPrice <= 0 || cashSettleStrikePrice <= 0) {
      alert('قیمت پایانی و قیمت اعمال باید بیشتر از صفر باشند');
      return;
    }
    if (!cashSettleSelectAll) {
      alert('لطفاً گزینه «انتخاب همه قراردادها» را فعال کنید');
      return;
    }

    const stats = getOpenOptionStats();
    const contractSize = cashSettleContractSize || 1000;
    const totalShares = cashSettleContracts * contractSize;
    if (totalShares > stats.totalShares) {
      alert(`تعداد قراردادها از موقعیت باز (${Math.floor(stats.totalShares / contractSize)}) بیشتر است`);
      return;
    }

    const isCall = selectedSymbol.startsWith('ض');
    const priceDiff = isCall
      ? Math.max(0, cashSettleFinalPrice - cashSettleStrikePrice)
      : Math.max(0, cashSettleStrikePrice - cashSettleFinalPrice);
    const gross = priceDiff * totalShares;
    const cashSettleComm = getSettingValue('option_cash_settle_comm', 0.00103);
    const optionSellComm = getSettingValue('option_sell_comm', 0.00103);
    const commission = gross * cashSettleComm;
    const net = gross - commission;

    if (gross <= 0) {
      alert('این آپشن در حالت اشباع (Out of the Money) است؛ تسویه نقدی مبلغی ندارد.');
      return;
    }

    try {
      setSellLoading(true);
      const adjustedPrice = optionSellComm < 1 ? (net / (totalShares * (1 - optionSellComm))) : 0;
      const sells = distributeSellQuantityAcrossSteps(totalShares, stats.rows);
      for (const s of sells) {
        await api.post('/api/transactions', {
          Date: new Date().toISOString().split('T')[0],
          Symbol: selectedSymbol,
          Type: 'sell',
          MarketType: 'Option',
          Quantity: s.qty,
          Price: adjustedPrice,
          Step: s.step,
          BlockedAmount: 0,
          Note: `تسویه نقدی ${cashSettleContracts} قرارداد`
        });
      }
      alert(`✅ تسویه نقدی با خالص ${Math.round(net).toLocaleString()} ریال ثبت شد`);
      setShowCashSettleModal(false);
      await fetchTransactions();
    } catch (error) {
      console.error(error);
      alert('خطا در ثبت تسویه نقدی');
    } finally {
      setSellLoading(false);
    }
  };

  const handlePhysicalSettleSubmitSymbolLevel = async () => {
    if (physicalSettleContracts <= 0) {
      alert('تعداد قرارداد باید بیشتر از صفر باشد');
      return;
    }
    if (physicalSettleFinalPrice <= 0 || physicalSettleStrikePrice <= 0) {
      alert('قیمت پایانی و قیمت اعمال باید بیشتر از صفر باشند');
      return;
    }
    if (!physicalSettleSelectAll) {
      alert('لطفاً گزینه «انتخاب همه قراردادها» را فعال کنید');
      return;
    }

    const stats = getOpenOptionStats();
    const contractSize = physicalSettleContractSize || 1000;
    const totalShares = physicalSettleContracts * contractSize;
    if (totalShares > stats.totalShares) {
      alert(`تعداد قراردادها از موقعیت باز (${Math.floor(stats.totalShares / contractSize)}) بیشتر است`);
      return;
    }

    const settlementAmount = totalShares * physicalSettleStrikePrice;
    const physicalCommRate = getSettingValue('option_physical_settle_comm', 0.0055);
    const physicalCommission = settlementAmount * physicalCommRate;
    const premiumAndOppCost = (stats.avgCostPerShare * totalShares) + (stats.totalOppCost * (totalShares / Math.max(1, stats.totalShares)));
    const totalPayable = settlementAmount + physicalCommission + premiumAndOppCost;
    const costPerShare = totalPayable / totalShares;
    const bourseBuyComm = getSettingValue('bourse_buy_comm', 0.003712);
    const adjustedBuyPrice = totalPayable / (totalShares * (1 + bourseBuyComm));

    try {
      setSellLoading(true);
      // 1. بستن پوزیشن آپشن در قیمت میانگین هزینه (سود/زیان آپشن ~ صفر شود و هزینه به سهم پایه منتقل شود)
      const sells = distributeSellQuantityAcrossSteps(totalShares, stats.rows);
      for (const s of sells) {
        await api.post('/api/transactions', {
          Date: new Date().toISOString().split('T')[0],
          Symbol: selectedSymbol,
          Type: 'sell',
          MarketType: 'Option',
          Quantity: s.qty,
          Price: stats.avgCostPerShare,
          Step: s.step,
          BlockedAmount: 0,
          Note: `تسویه فیزیکی ${physicalSettleContracts} قرارداد`
        });
      }

      // 2. باز کردن پله خرید سهم پایه با قیمت تنظیم‌شده (کارمزد خرید در محاسبات تسویه لحاظ شده)
      const base = getBaseSymbolForOption(selectedSymbol);
      await api.post('/api/transactions', {
        Date: new Date().toISOString().split('T')[0],
        Symbol: base,
        Type: 'buy',
        MarketType: 'Bourse',
        Quantity: totalShares,
        Price: adjustedBuyPrice,
        Step: `تسویه فیزیکی ${selectedSymbol}`,
        BlockedAmount: 0,
        Note: `تسویه فیزیکی آپشن ${selectedSymbol} @ ${physicalSettleStrikePrice}`
      });

      alert(`✅ تسویه فیزیکی انجام شد.\nقیمت تمام‌شده هر سهم: ${costPerShare.toFixed(2)} ریال\nتعداد سهم دریافتی: ${totalShares.toLocaleString()}`);
      setShowPhysicalSettleModal(false);
      await fetchTransactions();
    } catch (error) {
      console.error(error);
      alert('خطا در ثبت تسویه فیزیکی');
    } finally {
      setSellLoading(false);
    }
  };

  // ref برای جلوگیری از ثبت دو باره
  const lastSellDataRef = useRef('');

    useEffect(() => {
      fetchTransactions();
    }, [showOnlyActive]);

  // پاک کردن تیک‌های انتخابی وقتی نماد عوض می‌شود
  useEffect(() => {
    setSelectedSteps([]);
  }, [selectedSymbol]);

  // پاک کردن داده‌های آپشن وقتی نماد تغییر می‌کند
  useEffect(() => {
    setOptionData(null);
    setIntrinsicValue(0);
    setOptionGreeks(null);
    setUnderlyingPrice(0);
    setPreviousPremium(0);
    previousPremiumEdited.current = false;
    setPreviousObservationDate('');
    setOptionInputs(null);
  }, [selectedSymbol]);

  // انتخاب خودکار نماد پیش‌انتخاب‌شده از گزارش پله‌های سودده
  useEffect(() => {
    if (preselectedSymbol) {
      setSelectedSymbol(preselectedSymbol);
      // کمی تاخیر برای اطمینان از set شدن state قبل از clear کردن در parent
      setTimeout(() => {
        onPreselectedUsedRef.current?.();
      }, 100);
    }
  }, [preselectedSymbol]);

  // Recalculate from the same current inputs shown on screen.
  useEffect(() => {
    if (!optionInputs || optionInputs.symbol !== selectedSymbolRef.current) return;
    const dates = optionInputs.quoteContext;
    const result = dates?.available ? calculateOptionValueFromAPI(
      optionInputs.symbol, optionInputs.underlyingPrice, currentPrice,
      optionInputs.strikePrice, optionInputs.maturityDate, optionInputs.baseSymbol,
      optionInputs.optionType, previousPremium, optionInputs.previousUnderlyingPrice,
      0.40, typeof optionRate === 'number' ? optionRate / 100 : NaN, null,
      { previousDate: dates.previousDate, asOf: dates.asOf }
    ) : { isValid: false, reason: dates?.reason || 'تاریخ قیمت نامشخص است' };
    setOptionData(result);
    setIntrinsicValue(result.isValid ? Number(result.intrinsicValue) : 0);
    setOptionGreeks(result.greeks || null);
  }, [optionInputs, previousPremium, currentPrice, optionRate, previousObservationDate]);

    // تایمر برای به‌روزرسانی خودکار قیمت هر 30 ثانیه
  useEffect(() => {
    if (!selectedSymbol) return;
    
    // تابع دریافت قیمت
    const updatePrice = () => {
      fetchMarketPrice(selectedSymbol);
    };
    
    // دریافت فوری قیمت
    updatePrice();
    
    // تنظیم تایمر برای به‌روزرسانی هر 30 ثانیه
    const interval = setInterval(updatePrice, 30000);
    
    // پاک کردن تایمر هنگام خروج از کامپوننت یا تغییر نماد
    return () => {
      clearInterval(interval);
      quoteSequence.current++;
    };
  }, [selectedSymbol]);

        const fetchTransactions = async () => {
    try {
      const data = await api.get('/api/transactions');
      const nonFutureData = data.filter(t => t.MarketType !== 'Future');
      setTransactions(nonFutureData);
      
      // محاسبه دقیق موقعیت‌های باز شامل خریدهای عادی و فروش‌های فزاینده (Short Sell)
      const symbolsQtyMap = {};
      nonFutureData.forEach(tx => {
        const sym = tx.Symbol;
        if (!symbolsQtyMap[sym]) symbolsQtyMap[sym] = 0;
        
        const type = tx.Type.toLowerCase();
        const qty = parseFloat(tx.Quantity) || 0;
        
        if (type === 'buy' || type === 'bonus' || type === 'short_sell') {
          symbolsQtyMap[sym] += qty;
        } else if (type === 'sell') {
          symbolsQtyMap[sym] -= qty;
        }
      });
      
      // استخراج نمادهای باز و همه نمادهای تاریخچه
      const activeSyms = Object.keys(symbolsQtyMap).filter(sym => Math.abs(symbolsQtyMap[sym]) > 0.01);
      const allSyms = [...new Set(nonFutureData.map(t => t.Symbol))];
      
      setActiveSymbols(activeSyms);
      setAllSymbols(allSyms);
      
      const currentList = showOnlyActive ? activeSyms : allSyms;
      setSymbols(currentList);
      
      if (currentList.length > 0 && !selectedSymbolRef.current) {
        setSelectedSymbol(currentList[0]);
      }

    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };



  const fetchMarketPrice = async (symbol) => {
    const requestId = ++quoteSequence.current;
    const current = () => requestId === quoteSequence.current && symbol === selectedSymbolRef.current;
    const clearOption = () => { setOptionInputs(null); setOptionData(null); setIntrinsicValue(0); setOptionGreeks(null); setUnderlyingPrice(0); };
    try {
      const data = await api.get(`/api/market_prices/${encodeURIComponent(symbol)}`);
      if (!current()) return;
      if (!(data?.price > 0)) {
        setCurrentPrice(getLastTransactionPrice(symbol)); setLastUpdate('آخرین معامله'); clearOption(); return;
      }
      const isOption = symbol.startsWith('ض') || symbol.startsWith('ط');
      if (!isOption || !data.strike_price || !data.maturity_date || !data.base_symbol) {
        setCurrentPrice(data.price); setLastUpdate(data.last_update || ''); clearOption(); return;
      }
      const underlying = await api.get(`/api/market_prices/${encodeURIComponent(data.base_symbol)}`);
      if (!current()) return;
      // Commit the option and its underlying together; never substitute an old user trade.
      const underlyingValue = Number(underlying?.price) || 0;
      const previousUnderlying = Number(underlying?.price_yesterday) || 0;
      const type = String(data.option_type).toLowerCase();
      setCurrentPrice(data.price); setLastUpdate(data.last_update || '');
      setUnderlyingPrice(underlyingValue);
      setOptionInputs({ symbol, quoteContext: datedQuoteContext(data.quote_metadata, underlying.quote_metadata), underlyingPrice: underlyingValue, previousUnderlyingPrice: previousUnderlying,
        optionPrice: data.price, strikePrice: data.strike_price, maturityDate: data.maturity_date,
        baseSymbol: data.base_symbol, optionType: type === 'call' || type === 'put' ? type : symbol.startsWith('ض') ? 'call' : 'put' });
      if (!previousPremiumEdited.current) setPreviousPremium(data.price_yesterday || 0);
    } catch (error) {
      if (!current()) return;
      setCurrentPrice(getLastTransactionPrice(symbol)); setLastUpdate('آخرین معامله'); clearOption();
      console.error('Error fetching option quote:', error);
    }
  };

  const getLastTransactionPrice = (symbol) => {
    const symbolTxs = transactions.filter(t => t.Symbol === symbol);
    if (symbolTxs.length > 0) {
      return symbolTxs[symbolTxs.length - 1].Price;
    }
    return 0;
  };

    const getCommissionRate = (marketType, transactionType) => {
    if (transactionType === 'bonus') return 0;
    const settingsMap = {};
    // مقادیر باید حتما به عدد تبدیل شوند تا در محاسبات اعشاری خطا ندهند
    settings.forEach(s => { settingsMap[s.key] = parseFloat(s.value) || 0; });
    
    // در فروش فزاینده، کارمزد همان کارمزد فروش عادی است
    const typeKey = transactionType === 'short_sell' ? 'sell' : transactionType;
    const key = `${marketType}_${typeKey}_comm`.toLowerCase();
    
    return settingsMap[key] || 0;
  };


  const getBankInterestRate = () => {
    const settingsMap = {};
    settings.forEach(s => { settingsMap[s.key] = s.value; });
    return settingsMap['bank_interest_rate'] || 0.20;
  };

  const formatPercent = (value) => {
    const formatted = Math.abs(value).toFixed(2);
    return value < 0 ? `-${formatted}%` : `${formatted}%`;
  };

    const calculateStocksLogic = (symbol, currentPrice, filter) => {
    const symbolTx = transactions.filter(t => t.Symbol === symbol);
    if (symbolTx.length === 0) return { rows: [], totalInvested: 0, totalMarketPnl: 0, totalOppCost: 0 };
    
    const bankRate = getBankInterestRate();
    const uniqueSteps = [...new Set(symbolTx.map(t => t.Step))];
    
    const displayRows = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let grandInvested = 0;
    let grandPnl = 0;
    let grandOpp = 0;
    
    for (const step of uniqueSteps) {
      const stepTx = symbolTx.filter(t => t.Step === step).sort((a, b) => new Date(a.Date) - new Date(b.Date));
      if (stepTx.length === 0) continue;
      
      const firstDate = new Date(stepTx[0].Date);
      const lastDate = new Date(stepTx[stepTx.length - 1].Date);
      
      let runningInvested = 0;
      let runningQty = 0;
      let totalBuyCost = 0;
      let totalSellRevenue = 0;
      let accumulatedOppCost = 0;
      let lastCalcDate = null;
      let marketType = 'Bourse';
      let isShortSell = false;
      let initialBlockedAmount = 0;
      
            for (const tx of stepTx) {
        const currDate = new Date(tx.Date);
        const rtype = tx.Type;
        const qty = parseFloat(tx.Quantity);
        const price = parseFloat(tx.Price);
        const mType = tx.MarketType;
        marketType = mType;
        
        const currentComm = getCommissionRate(mType, rtype);
        
        if (rtype === 'short_sell') {
          isShortSell = true;
          initialBlockedAmount = parseFloat(tx.BlockedAmount) || 0;
        }

        if (lastCalcDate !== null) {
          const diffTime = Math.abs(currDate - lastCalcDate);
          const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          // برای فروش فزاینده (short) هزینه فرصت محاسبه نمی‌شود
          if (days > 0 && !isShortSell && (runningInvested > 0 || initialBlockedAmount > 0)) {
            accumulatedOppCost += runningInvested * bankRate * (days / 365);
          }
        }
        lastCalcDate = currDate;
        
        if (rtype === 'buy') {

          const cost = (qty * price) * (1 + currentComm);
          if (isShortSell) {
            // در صورت داشتن پوزیشن باز فروش فزاینده، تراکنش خرید آن را تسویه و کم می‌کند
            runningQty -= qty;
            totalSellRevenue += cost; // هزینه خرید (تسویه پوزیشن شورت) به خروجی‌ها اضافه می‌شود
          } else {
            totalBuyCost += cost;
            runningInvested += cost;
            runningQty += qty;
          }
        } else if (rtype === 'sell' || rtype === 'short_sell') {
          const rev = (qty * price) * (1 - currentComm);
          if (rtype === 'short_sell') {
            totalBuyCost += rev; // وجه دریافتی بابت پیش‌فروش اولیه آپشن
            runningQty += qty; // ایجاد موقعیت باز با فروش فزاینده
            runningInvested += initialBlockedAmount; // سرمایه درگیر همان وجه تضمین است
          } else if (isShortSell) {
            // بستن پوزیشن فروش فزاینده با خرید - باید هزینه خرید را حساب کنیم
            const cost = (qty * price) * (1 + currentComm);
            totalBuyCost += cost;
            runningQty -= qty;
          } else {
            // بستن پوزیشن خرید عادی با فروش
            totalSellRevenue += rev;
            const oldQty = runningQty;
            runningQty -= qty;
            if (oldQty > 0) {
              runningInvested = runningInvested * (runningQty / oldQty);
            }
          }
          if (Math.abs(runningQty) < 0.01) {
            runningQty = 0;
            runningInvested = 0;
          }
        } else if (rtype === 'bonus') {
          runningQty += qty;
        } else if (rtype === 'margin') {
          runningInvested += parseFloat(tx.BlockedAmount) || 0;
        }
      }
      
            // --- محاسبه روزهای نگهداری ---
      let daysHeld = 0;
      if (runningQty > 0) {
        const diffTime = Math.abs(today - lastCalcDate);
        const daysToNow = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        // برای فروش فزاینده (short) هزینه فرصت محاسبه نمی‌شود
        if (daysToNow > 0 && !isShortSell) {
          accumulatedOppCost += runningInvested * bankRate * (daysToNow / 365);
        }
        const diffFirst = Math.abs(today - firstDate);
        daysHeld = Math.ceil(diffFirst / (1000 * 60 * 60 * 24));
      } else {
        const diffLast = Math.abs(lastDate - firstDate);
        daysHeld = Math.ceil(diffLast / (1000 * 60 * 60 * 24));
      }

      const commSell = getCommissionRate(marketType, 'sell');
      const commBuy = getCommissionRate(marketType, 'buy');

      // --- تشخیص میزان فروش رفته برای تفکیک ردیف ---
      const hasPartialSell = totalSellRevenue > 0 && runningQty > 0;

      // تابع کمکی برای اضافه کردن ردیف به جدول
      const addRow = (rowStatus, rowStepName, rowQty, rowInvested, rowMarketPnl, rowOppCost) => {
        let show = false;
        if (filter === 'همه') show = true;
        else if (filter === '🟢 باز' && rowStatus === '🟢 باز') show = true;
        else if (filter === '🔴 بسته شده' && rowStatus === '🔴 بسته شده') show = true;

        if (show && rowQty >= 0) {
          // برای فروش فزاینده (short) هزینه فرصت محاسبه نمی‌شود
          const net = isShortSell ? rowMarketPnl : rowMarketPnl - rowOppCost;
          
          displayRows.push({
            step: rowStepName,
            status: rowStatus,
            daysHeld: daysHeld,
            remainingQty: parseInt(rowQty),
            invested: Math.round(rowInvested),
            marketPnl: Math.round(rowMarketPnl),
            oppCost: Math.round(rowOppCost),
            netPnl: Math.round(net),
            rawPnl: Math.round(net),
            rawInvested: Math.round(rowInvested),
            marketType: marketType,
            isShortSell: isShortSell
          });

          if (rowStatus === '🟢 باز') {
            grandInvested += rowInvested;
          }
          grandPnl += rowMarketPnl;
          grandOpp += rowOppCost;
        }
      };

      if (Math.abs(runningQty) < 0.01) {
        // حالت ۱: پله کاملاً بسته شده است
        let marketPnl = isShortSell ? (totalBuyCost - totalSellRevenue) : (totalSellRevenue - totalBuyCost);
        const inv = isShortSell ? totalBuyCost : totalBuyCost; // مبنای محاسبه برای بسته شده‌ها
        addRow('🔴 بسته شده', step, 0, inv, marketPnl, accumulatedOppCost);
      } 
      else if (hasPartialSell) {
        // حالت ۲: فروش بخشی از پله (تفکیک به دو ردیف بسته و باز)
        
        // الف) محاسبه بخش بسته شده
        let closedMarketPnl = 0;
        let closedInvested = 0;
        let closedOppCost = accumulatedOppCost * 0.5; // تخصیص تقریبی هزینه فرصت به بخش بسته شده
        
        if (isShortSell) {
          closedInvested = totalBuyCost;
          closedMarketPnl = totalBuyCost - totalSellRevenue; 
        } else {
          // محاسبه تناسبی بهای تمام شده برای بخش فروخته شده
          const estTotalQty = runningQty + (totalSellRevenue / currentPrice);
          const ratio = 1 - (runningQty / (estTotalQty > 0 ? estTotalQty : runningQty + 1));
          closedInvested = totalBuyCost * ratio;
          closedMarketPnl = totalSellRevenue - closedInvested;
        }
        
        addRow('🔴 بسته شده', `${step} (بخش فروخته شده)`, 0, closedInvested, closedMarketPnl, closedOppCost);

        // ب) محاسبه بخش باز (باقیمانده)
        let openMarketPnl = 0;
        let openOppCost = accumulatedOppCost - closedOppCost;
        
        if (isShortSell) {
          const avgSellPrice = totalBuyCost > 0 ? (totalBuyCost / (runningQty + 1)) : 0;
          const originalValue = runningQty * avgSellPrice; 
          const currentCostToBuy = (runningQty * currentPrice) * (1 + commBuy);
          openMarketPnl = originalValue - currentCostToBuy;
        } else {
          const currentVal = (runningQty * currentPrice) * (1 - commSell);
          openMarketPnl = currentVal - runningInvested;
        }
        
        addRow('🟢 باز', `${step} (باقیمانده)`, runningQty, runningInvested, openMarketPnl, openOppCost);
      } 
      else {
        // حالت ۳: پله کاملاً باز است (هیچ فروشی نداشته)
        let marketPnl = 0;
        if (isShortSell) {
          const avgSellPrice = totalBuyCost > 0 ? (totalBuyCost / runningQty) : 0;
          const originalValue = runningQty * avgSellPrice; 
          const currentCostToBuy = (runningQty * currentPrice) * (1 + commBuy);
          marketPnl = originalValue - currentCostToBuy;
        } else {
          const currentVal = (runningQty * currentPrice) * (1 - commSell);
          marketPnl = currentVal - runningInvested;
        }
        
        addRow('🟢 باز', step, runningQty, runningInvested, marketPnl, accumulatedOppCost);
      }

    }
    
    return { rows: displayRows, totalInvested: grandInvested, totalMarketPnl: grandPnl, totalOppCost: grandOpp };
  };

    const handleCalculate = () => {
    setLoading(true);
    setTimeout(() => {
      // اگر "همه" انتخاب شده، برای همه نمادها محاسبه کن
      const isAllSymbols = selectedSymbol === 'همه' || selectedSymbol === 'ALL' || selectedSymbol === '';
      const symbolsToProcess = isAllSymbols ? [...new Set(transactions.map(t => t.Symbol))] : [selectedSymbol];

      let allRows = [];
      let grandTotalInvested = 0;
      let grandTotalMarketPnl = 0;
      let grandTotalOppCost = 0;
      let grandGrandNet = 0;

      // محاسبه برای هر نماد
      symbolsToProcess.forEach(sym => {
        // پیدا کردن قیمت جاری برای این نماد
        const symPrice = isAllSymbols ?
          (async () => {
            try {
              const data = await api.get(`/api/market_prices/${encodeURIComponent(sym)}`);
              return data?.price || 0;
            } catch { return 0; }
          })() : currentPrice;

        // اگر همه نمادها، باید قیمت را جداگانه بگیریم
        if (isAllSymbols) {
          // این بخش نیاز به پردازش async دارد
        }
      });

      // روش ساده‌تر: اگر همه نمادها انتخاب شده، از API قیمت‌ها بگیریم
      let priceMap = {};

      const doCalculation = async () => {
        if (isAllSymbols) {
          // گرفتن قیمت‌ها برای همه نمادها
          const pricePromises = symbolsToProcess.map(async (sym) => {
            try {
              const data = await api.get(`/api/market_prices/${encodeURIComponent(sym)}`);
              return { symbol: sym, price: data?.price || 0 };
            } catch {
              return { symbol: sym, price: 0 };
            }
          });
          const priceResults = await Promise.all(pricePromises);
          priceResults.forEach(p => { priceMap[p.symbol] = p.price; });
        }

        // محاسبه برای هر نماد
        symbolsToProcess.forEach(sym => {
          const price = isAllSymbols ? (priceMap[sym] || 0) : currentPrice;
          const result = calculateStocksLogic(sym, price, viewFilter);

          // اضافه کردن نام نماد به هر ردیف
          result.rows.forEach(row => {
            allRows.push({
              ...row,
              symbolName: sym
            });
          });

          grandTotalInvested += result.totalInvested;
          grandTotalMarketPnl += result.totalMarketPnl;
          grandTotalOppCost += result.totalOppCost;
        });

        grandGrandNet = allRows.reduce((sum, r) => sum + r.netPnl, 0);

        // ادامه همانند قبل...
        const processedRows = allRows.map(row => {
          let percent = 0;
          if (row.rawInvested !== 0) {
            percent = (row.rawPnl / row.rawInvested) * 100;
          }
          return {
            ...row,
            investedFormatted: Math.round(row.invested).toLocaleString(),
            marketPnlFormatted: Math.round(row.marketPnl).toLocaleString(),
            oppCostFormatted: Math.round(row.oppCost).toLocaleString(),
            netPnlFormatted: Math.round(row.netPnl).toLocaleString(),
            percentFormatted: formatPercent(percent),
            percentValue: percent
          };
        });

        // اعمال فیلتر "فقط پله‌های با عملکرد مثبت"
        let filteredRows = processedRows;
        if (showOnlyPositive) {
          filteredRows = processedRows.filter(row => row.netPnl > 0);
        }

        // مرتب‌سازی بر اساس netPnl (بیشترین سود اول)
        filteredRows.sort((a, b) => b.netPnl - a.netPnl);

        const filteredTotalInvested = filteredRows.reduce((sum, r) => sum + r.invested, 0);
        const filteredTotalMarketPnl = filteredRows.reduce((sum, r) => sum + r.marketPnl, 0);
        const filteredTotalOppCost = filteredRows.reduce((sum, r) => sum + r.oppCost, 0);
        const filteredGrandNet = filteredRows.reduce((sum, r) => sum + r.netPnl, 0);

        setResultData({
          rows: filteredRows,
          totalRemainingQty: filteredRows.reduce((sum, r) => sum + r.remainingQty, 0),
          filter: viewFilter
        });

        setSummary({
          totalInvested: Math.round(filteredTotalInvested),
          totalMarketPnl: Math.round(filteredTotalMarketPnl),
          totalOppCost: Math.round(filteredTotalOppCost),
          grandNet: Math.round(filteredGrandNet),
          selectedSymbol: isAllSymbols ? 'همه نمادها' : selectedSymbol
        });

        setLoading(false);
      };

      // اجرای محاسبات
      if (isAllSymbols) {
        // برای حالت "همه"، باید async عمل کنیم
        doCalculation().then(() => {}).catch(err => {
          console.error('Error calculating all symbols:', err);
          setLoading(false);
        });
      } else {
        // برای حالت تک نماد، همانند قبل
        const { rows, totalInvested, totalMarketPnl, totalOppCost } = calculateStocksLogic(selectedSymbol, currentPrice, viewFilter);

        const totalRemainingQty = rows.reduce((sum, r) => sum + r.remainingQty, 0);

        const grandNet = rows.reduce((sum, r) => sum + r.netPnl, 0);


        const processedRows = rows.map(row => {
          let percent = 0;
          if (row.rawInvested !== 0) {
            percent = (row.rawPnl / row.rawInvested) * 100;
          }
          return {
            ...row,
            investedFormatted: Math.round(row.invested).toLocaleString(),
            marketPnlFormatted: Math.round(row.marketPnl).toLocaleString(),
            oppCostFormatted: Math.round(row.oppCost).toLocaleString(),
            netPnlFormatted: Math.round(row.netPnl).toLocaleString(),
            percentFormatted: formatPercent(percent),
            percentValue: percent
          };
        });

        // اعمال فیلتر "فقط پله‌های با عملکرد مثبت"
        let filteredRows = processedRows;
        if (showOnlyPositive) {
          filteredRows = processedRows.filter(row => row.netPnl > 0);
        }

        const filteredTotalInvested = filteredRows.reduce((sum, r) => sum + r.invested, 0);
        const filteredTotalMarketPnl = filteredRows.reduce((sum, r) => sum + r.marketPnl, 0);
        const filteredTotalOppCost = filteredRows.reduce((sum, r) => sum + r.oppCost, 0);
        const filteredGrandNet = filteredRows.reduce((sum, r) => sum + r.netPnl, 0);

        setResultData({
          rows: filteredRows,
          totalRemainingQty: filteredRows.reduce((sum, r) => sum + r.remainingQty, 0),
          filter: viewFilter
        });

        setSummary({
          totalInvested: Math.round(filteredTotalInvested),
          totalMarketPnl: Math.round(filteredTotalMarketPnl),
          totalOppCost: Math.round(filteredTotalOppCost),
          grandNet: Math.round(filteredGrandNet),
          selectedSymbol: selectedSymbol
        });

        setLoading(false);
      }
    }, 100);
  };

  // این بخش باعث می‌شود با هر تغییری، محاسبات خودکار انجام شود
  useEffect(() => {
    if (transactions.length > 0 && selectedSymbol) {
      handleCalculate();
    }
  }, [selectedSymbol, currentPrice, viewFilter, showOnlyPositive, transactions]);

  // انتخاب خودکار نماد ارسال‌شده از گزارش پله‌های سودده
  useEffect(() => {
    if (preselectedSymbol && symbols.length > 0) {
      // بررسی اینکه نماد در لیست موجود است
      if (symbols.includes(preselectedSymbol) || allSymbols.includes(preselectedSymbol)) {
        setSelectedSymbol(preselectedSymbol);
        // فراخوانی callback برای پاک کردن نماد انتخاب‌شده در والد
        if (onPreselectedUsed) {
          onPreselectedUsed();
        }
      }
    }
  }, [preselectedSymbol, symbols, allSymbols]);


    // --- توابع فروش گروهی ---
  const toggleStepSelection = (step) => {
    setSelectedSteps(prev => 
      prev.includes(step) ? prev.filter(s => s !== step) : [...prev, step]
    );
  };

  const selectPositiveSteps = () => {
    if (!resultData) return;
    const positiveSteps = resultData.rows
      .filter(r => r.status === '🟢 باز' && r.netPnl > 0)
      .map(r => r.step);
    setSelectedSteps(positiveSteps);
  };

    const openBulkModal = () => {
    if (selectedSteps.length === 0) {
      alert('لطفاً حداقل یک پله را برای فروش انتخاب کنید.');
      return;
    }
    setBulkSellPrice(currentPrice);
    setBulkSellDate(new Date().toISOString().split('T')[0]);
    setShowBulkModal(true);
  };

  const handleBulkSell = async () => {
    if (sellLoading) return;
    if (bulkSellPrice <= 0) {
      alert('قیمت فروش باید بیشتر از صفر باشد');
      return;
    }

    setSellLoading(true);
    // فقط پله‌های باز و انتخاب شده
    const rowsToSell = resultData.rows.filter(r => selectedSteps.includes(r.step) && r.status === '🟢 باز');
    
    try {
      for (const row of rowsToSell) {
        const sellTransaction = {
          Date: bulkSellDate,

          Symbol: selectedSymbol,
          Type: 'sell',
          MarketType: row.marketType || 'Bourse',
          Quantity: row.remainingQty, // کل مانده این پله را می‌فروشد
          Price: bulkSellPrice,
          Step: row.step,
          BlockedAmount: 0,
          Note: `فروش گروهی پله ${row.step}`
        };
        
        await api.post('/api/transactions', sellTransaction);
      }
      
      alert('✅ فروش گروهی با موفقیت انجام شد');
      setShowBulkModal(false);
      setSelectedSteps([]); // پاک کردن تیک‌ها
      await fetchTransactions(); // آپدیت لیست
    } catch (error) {
      console.error('Error bulk selling:', error);
      alert('❌ خطا در ارتباط با سرور هنگام فروش گروهی');
    } finally {
      setSellLoading(false);
    }
  };
    // -----------------------

  // باز کردن مودال فروش
  const openSellModal = (row) => {
    if (row.status !== '🟢 باز') {
      alert('این پله قبلاً بسته شده است');
      return;
    }

    if (sellLoading) {
      alert('در حال ثبت فروش قبلی هستید، لطفاً صبر کنید');
      return;
    }
    setSelectedStepForSell(row);
    setSellPrice(currentPrice);
    setSellQuantity(row.remainingQty);
    setShowSellModal(true);
  };

  // ثبت فروش با جلوگیری از ثبت تکراری
  const handleSell = async () => {
    // بررسی نکردن با ref
    if (sellLoading) {
      console.log('در حال ثبت است، صرف نظر شد');
      return;
    }
    
    if (!selectedStepForSell) {
      console.log('no selected step');
      return;
    }
    
    if (sellQuantity <= 0) {
      alert('تعداد فروش باید بیشتر از صفر باشد');
      return;
    }
    
    if (sellQuantity > selectedStepForSell.remainingQty) {
      alert(`تعداد فروش نمی‌تواند بیشتر از ${selectedStepForSell.remainingQty} باشد`);
      return;
    }
    
    if (sellPrice <= 0) {
      alert('قیمت فروش باید بیشتر از صفر باشد');
      return;
    }
    
    // ایجاد یک کلید یکتا برای این فروش
    const sellKey = `${selectedSymbol}_${selectedStepForSell.step}_${sellQuantity}_${sellPrice}_${Date.now()}`;
    
    // بررسی اینکه آیا همین فروش در 2 ثانیه اخیر ثبت شده
    const now = Date.now();
    if (lastSellTimeRef.current && (now - lastSellTimeRef.current) < 2000) {
      if (lastSellDataRef.current === sellKey) {
        console.log('تکراری تشخیص داده شد، صرف نظر شد');
        alert('این درخواست قبلاً ثبت شده است');
        return;
      }
    }
    
    // قفل کردن
    setSellLoading(true);
    lastSellTimeRef.current = now;
    lastSellDataRef.current = sellKey;
    
    const sellTransaction = {
      Date: new Date().toISOString().split('T')[0],
      Symbol: selectedSymbol,
      Type: 'sell',
      MarketType: selectedStepForSell.marketType || 'Bourse',
      Quantity: sellQuantity,
      Price: sellPrice,
      Step: selectedStepForSell.step,
      BlockedAmount: 0,
      Note: `فروش از پله ${selectedStepForSell.step}`
    };
    
    try {
      console.log('ارسال درخواست فروش...');
      await api.post('/api/transactions', sellTransaction);

      console.log('فروش با موفقیت ثبت شد');
      alert(`✅ فروش ${sellQuantity} عدد از ${selectedStepForSell.step} با قیمت ${sellPrice.toLocaleString()} ریال ثبت شد`);

      // بستن مودال
      setShowSellModal(false);
      setSelectedStepForSell(null);

      // به‌روزرسانی داده‌ها
      await fetchTransactions();

      // محاسبه مجدد
      handleCalculate();
    } catch (error) {
      console.error('Error selling:', error);
      alert('❌ خطا در ارتباط با سرور');
    } finally {
      // آزاد کردن قفل
      setSellLoading(false);
    }
  };

  const getStockReturnPercent = () => {
    if (!summary || summary.totalInvested === 0) return 0;
    return (summary.totalMarketPnl / summary.totalInvested) * 100;
  };

  const getBankReturnPercent = () => {
    if (!summary || summary.totalInvested === 0) return 0;
    return (summary.totalOppCost / summary.totalInvested) * 100;
  };

  const getDiffPercent = () => {
    return getStockReturnPercent() - getBankReturnPercent();
  };

  const exportToExcel = () => {
    if (!resultData || resultData.rows.length === 0) {
      alert('داده‌ای برای خروجی وجود ندارد');
      return;
    }

    const excelData = resultData.rows.map(row => ({
      'نماد': selectedSymbol,
      'پله': row.step,
      'وضعیت': row.status,
      'تعداد روز': row.daysHeld,
      'تعداد مانده': row.remainingQty,
      'سرمایه درگیر': row.investedFormatted,
      'سود/زیان بازار': row.marketPnlFormatted,
      'هزینه فرصت': row.oppCostFormatted,
      'عملکرد خالص': row.netPnlFormatted,
      'درصد عملکرد': row.percentFormatted
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'تحلیل سهام');
    XLSX.writeFile(workbook, `تحلیل_${selectedSymbol}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (transactions.length === 0) {
    return (
      <div className="stock-analysis">
        <h1>📊 تحلیل پورتفوی (سهام و طلا)</h1>
        <div className="info-box">داده‌ای موجود نیست.</div>
      </div>
    );
  }

  return (
    <div className="stock-analysis">
      <h1>📊 تحلیل پورتفوی (سهام و طلا)</h1>
      
      <div className="filters-section">
        <div className="filters-toolbar">
          <div className="toolbar-left">
            <div className="filter-group-compact symbol-group">
              <label className="filter-label-compact">نماد</label>
              <div className="filter-input-wrapper">
                <span className="filter-icon">🔎</span>
                <input
                  list="symbols-list"
                  className="filter-input"
                  value={selectedSymbol}
                  onChange={(e) => setSelectedSymbol(e.target.value)}
                  onClick={() => setSelectedSymbol('')}
                  placeholder="جستجو..."
                />
                <datalist id="symbols-list">
                  <option key="ALL" value="همه" />
                  {symbols.map(sym => <option key={sym} value={sym} />)}
                </datalist>
              </div>
            </div>

            <div className="filter-group-compact price-group">
              <label className="filter-label-compact">قیمت {lastUpdate ? `(${lastUpdate})` : ''}</label>
              <div className="filter-input-wrapper">
                <input
                  type="number"
                  className="filter-input price-input"
                  value={currentPrice}
                  onChange={(e) => setCurrentPrice(parseFloat(e.target.value))}
                  step={100}
                />
                <span className="filter-suffix">ریال</span>
              </div>
            </div>

            {/* فیلد قیمت عادلانه لحظه‌ای آپشن */}
            {optionData?.isValid && optionData?.predictions?.referencePrice > 0 && (
              <div className="filter-group-compact intrinsic-value-group">
                <label className="filter-label-compact">
                  💰 قیمت مبنا با IV قبلی ({(() => {
                    const fairPrice = parseFloat(optionData.predictions.predictedTomorrowPrice) || 0;
                    const diffPercent = fairPrice > 0 ? ((currentPrice - fairPrice) / fairPrice) * 100 : 0;
                    const isOvervalued = diffPercent > 0;
                    const sign = diffPercent > 0 ? '+' : '';
                    const color = isOvervalued ? '#f44336' : '#4caf50';
                    return (
                      <span style={{ color: color, fontWeight: 'bold' }}>
                        {sign}{diffPercent.toFixed(2)}% {isOvervalued ? 'بالاتر از مبنا' : 'پایین‌تر از مبنا'}
                      </span>
                    );
                  })()})
                </label>
                <div className="filter-input-wrapper">
                  <input
                    type="number"
                    className="filter-input intrinsic-value-input"
                    value={parseFloat(optionData.predictions.predictedTomorrowPrice) || 0}
                    readOnly
                    style={{ background: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', fontWeight: 'bold' }}
                  />
                  <span className="filter-suffix">ریال</span>
                </div>
              </div>
            )}

            <div className="filter-group-compact toggle-group">
              <label className="filter-label-compact">فقط باز</label>
              <div className="toggle-switch-wrapper">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showOnlyActive}
                    onChange={(e) => setShowOnlyActive(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="filter-group-compact view-group">
              <label className="filter-label-compact">وضعیت</label>
              <div className="pill-group">
                <button
                  className={`pill-btn ${viewFilter === 'همه' ? 'active' : ''}`}
                  onClick={() => setViewFilter('همه')}
                >
                  همه
                </button>
                <button
                  className={`pill-btn ${viewFilter === '🟢 باز' ? 'active' : ''}`}
                  onClick={() => setViewFilter('🟢 باز')}
                >
                  🟢 باز
                </button>
                <button
                  className={`pill-btn ${viewFilter === '🔴 بسته شده' ? 'active' : ''}`}
                  onClick={() => setViewFilter('🔴 بسته شده')}
                >
                  🔴 بسته
                </button>
              </div>
            </div>

            <div className="filter-group-compact" style={{ marginRight: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showOnlyPositive}
                  onChange={(e) => setShowOnlyPositive(e.target.checked)}
                  style={{ transform: 'scale(1.2)' }}
                />
                <span style={{ fontWeight: 'bold', color: showOnlyPositive ? '#2e7d32' : '#666' }}>
                  ✅ فقط پله‌های با عملکرد مثبت
                </span>
              </label>
            </div>
          </div>

          <div className="toolbar-right">
            <button className="btn-export" onClick={exportToExcel}>
              📥 اکسل
            </button>
          </div>
        </div>
      </div>


      {/* بخش جزئیات آپشن و ارزش ذاتی - کشویی */}
      {optionInputs?.quoteContext && <p className="option-model-note">آخرین معامله آپشن: {sourceTime(optionInputs.quoteContext.optionTimestamp)} · سهم پایه: {sourceTime(optionInputs.quoteContext.baseTimestamp)} · تاریخ پایانی قبلی: {sourceDay(optionInputs.quoteContext.previousDate)}{optionInputs.quoteContext.available && ` · فاصله زمان دو معامله: ${optionInputs.quoteContext.timeGapSeconds} ثانیه؛ هم‌زمانی دقیق تضمین نشده است.`}</p>}
      {optionData && !optionData.isValid && <p role="status">{optionData.reason}</p>}
      {optionData && optionData.isValid && (
        <div className="option-analysis-section">
          {/* هدر کلیک‌پذیر برای باز/بسته کردن */}
          <div 
            className="option-analysis-header clickable-header"
            onClick={() => setIsOptionSectionOpen(!isOptionSectionOpen)}
            style={{ cursor: 'pointer' }}
          >
            <h3>
              <span style={{ marginLeft: '10px', display: 'inline-block', transition: 'transform 0.3s', transform: isOptionSectionOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                ▶
              </span>
              📈 تحلیل آپشن {selectedSymbol}
            </h3>
            <span className="option-type-badge">
              {optionData.optionType === 'call' ? 'اختیار خرید (Call)' : 'اختیار فروش (Put)'}
            </span>
          </div>

          {/* محتوای بخش - فقط در صورت باز بودن نمایش داده می‌شود */}
          {isOptionSectionOpen && (
            <>
              <div className="option-info-grid">
                <div className="option-info-card">
                  <div className="option-info-label">نماد پایه</div>
                  <div className="option-info-value">{optionData.baseSymbol}</div>
                </div>
                <div className="option-info-card">
                  <div className="option-info-label">قیمت سهم پایه</div>
                  <div className="option-info-value">{underlyingPrice.toLocaleString()} ریال</div>
                </div>
                <div className="option-info-card">
                  <div className="option-info-label">قیمت اعمال (Strike)</div>
                  <div className="option-info-value">{optionData.strikePrice.toLocaleString()} ریال</div>
                </div>
                <div className="option-info-card">
                  <div className="option-info-label">تاریخ سررسید (شمسی/میلادی)</div>
                  <div className="option-info-value">{optionData.maturityDate} / {optionData.maturityDateGregorian}</div>
                </div>
                <div className="option-info-card">
                  <div className="option-info-label">روز تقویمی تا سررسید</div>
                  <div className="option-info-value">{optionData.daysToMaturity} روز</div>
                </div>
                <div className="option-info-card" title="از فردا تا روز سررسید؛ پنجشنبه و جمعه حذف شده‌اند. تعطیلات رسمی و تعطیلی اضطراری لحاظ نشده‌اند.">
                  <div className="option-info-label">برآورد جلسات معاملاتی</div>
                  <div className="option-info-value">{remainingWeekdaySessions(optionData.maturityDate, optionInputs?.quoteContext?.asOf) ?? 'ناموجود'} جلسه</div>
                  <small>بدون پنجشنبه و جمعه؛ بدون احتساب تعطیلات رسمی</small>
                </div>
                <div className="option-info-card">
                  <div className="option-info-label">قیمت فعلی آپشن</div>
                  <div className="option-info-value">{currentPrice.toLocaleString()} ریال</div>
                </div>
              </div>

              {/* فیلد ورودی قیمت دیروز */}
              <div className="previous-premium-input" style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#aaa', fontSize: '0.9rem' }}>
                  قیمت پایانی مشاهده قبلی آپشن (ریال):
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="number"
                    aria-label="قیمت قبلی آپشن"
                    min="0"
                    value={previousPremium}
                    onChange={(e) => { previousPremiumEdited.current = true; setPreviousPremium(parseFloat(e.target.value) || 0); }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: '#2d2d3f',
                      color: 'white',
                      width: '150px'
                    }}
                    step="0.01"
                  />
                  <span style={{ color: '#888', fontSize: '0.85rem' }}>ریال</span>
                  {optionData.ivYesterday !== null && (
                    <span style={{ color: '#4caf50', fontSize: '0.85rem' }}>
                      IV مشاهده قبلی: {optionData.ivYesterdayPercent}%
                    </span>
                  )}
                  {!optionData.ivSolved && (
                    <span style={{ color: '#ff9800', fontSize: '0.85rem' }}>
                      IV حل نشد - Greeks محاسبه نمی‌شود
                    </span>
                  )}
                </div>
                <small style={{ color: '#666', fontSize: '0.75rem', marginTop: '5px', display: 'block' }}>
                  قیمت قبلی از API دریافت می‌شود؛ ویرایش شما تا تغییر نماد حفظ می‌شود. تاریخ مشاهده قبلی با تاریخچه رسمی تطبیق داده شده است؛ تعطیلات از اختلاف واقعی تاریخ‌ها محاسبه می‌شود.
                </small>
              </div>

              <div className="option-info-grid">
                <label className="option-info-card">تاریخ مشاهده قبلی (میلادی)
                  <input aria-label="تاریخ مشاهده قبلی" type="date" value={optionInputs?.quoteContext?.previousDate || previousObservationDate} disabled={!!optionInputs?.quoteContext?.available} onChange={e => setPreviousObservationDate(e.target.value)} />
                  <small>{optionData.previousDateAssumed ? 'تاریخ فرضی' : 'تاریخ تأییدشدهٔ منبع'}: {optionData.previousDate || 'نامعتبر'}</small>
                </label>
                <label className="option-info-card">نرخ سالانه مدل (%)
                  <input aria-label="نرخ سالانه مدل" type="number" min="0" max="200" step="0.1" value={optionRate} onChange={e => setOptionRate(Number(e.target.value))} />
                  <small>بهره پیوسته؛ پیش‌فرض فرضی ۳۰٪</small>
                </label>
              </div>
              <p className="option-model-note">مدل اختیار اروپایی، بدون سود تقسیمی؛ مبالغ و حساسیت‌ها به ازای هر واحد و به ریال است. تاریخ و ساعت آخرین معامله از منبع رسمی دریافت شده است؛ دریافت هم‌زمان دو قیمت به معنی انجام هم‌زمان معاملات نیست.</p>
              <div className="option-value-cards">
                <div className="option-value-card intrinsic">
                  <div className="option-value-label">ارزش ذاتی (Intrinsic Value)</div>
                  <div className="option-value-value">{parseFloat(optionData.intrinsicValue).toLocaleString()} ریال</div>
                </div>
                <div className="option-value-card time">
                  <div className="option-value-label">ارزش زمانی (Time Value)</div>
                  <div className="option-value-value">{parseFloat(optionData.timeValue).toLocaleString()} ریال</div>
                </div>
              </div>

              {/* IV Cards */}
              <div className="option-value-cards" style={{ marginTop: '15px' }}>
                <div className="option-value-card" style={{ background: optionData.ivSolved ? 'rgba(33, 150, 243, 0.15)' : 'rgba(255, 152, 0, 0.15)', border: `1px solid ${optionData.ivSolved ? 'rgba(33, 150, 243, 0.3)' : 'rgba(255, 152, 0, 0.3)'}` }}>
                  <div className="option-value-label">IV قیمت فعلی (مدل)</div>
                  <div className="option-value-value" style={{ color: optionData.ivSolved ? '#2196f3' : '#ff9800' }}>
                    {optionData.ivSolved ? `${optionData.ivTodayPercent}%` : 'حل نشد'}
                  </div>
                </div>
                {optionData.ivYesterday !== null && (
                  <div className="option-value-card" style={{ background: 'rgba(255, 152, 0, 0.15)', border: '1px solid rgba(255, 152, 0, 0.3)' }}>
                    <div className="option-value-label">IV مشاهده قبلی</div>
                    <div className="option-value-value" style={{ color: '#ff9800' }}>{optionData.ivYesterdayPercent}%</div>
                  </div>
                )}
                {optionData.ivSolved && optionData.deltaIVPercent !== 'N/A' && (
                  <div className="option-value-card" style={{
                    background: parseFloat(optionData.deltaIVPercent) >= 0 ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                    border: `1px solid ${parseFloat(optionData.deltaIVPercent) >= 0 ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`
                  }}>
                    <div className="option-value-label">ΔIV (واحد درصد)</div>
                    <div className="option-value-value" style={{ color: parseFloat(optionData.deltaIVPercent) >= 0 ? '#4caf50' : '#f44336' }}>
                      {parseFloat(optionData.deltaIVPercent) > 0 ? '+' : ''}{optionData.deltaIVPercent} واحد درصد
                    </div>
                  </div>
                )}
              </div>

              {!optionData.ivSolved && (
                <div className="warning-box" style={{
                  background: 'rgba(255, 152, 0, 0.2)',
                  border: '1px solid rgba(255, 152, 0, 0.5)',
                  padding: '12px 15px',
                  borderRadius: '8px',
                  margin: '15px 0',
                  color: '#ff9800',
                  fontSize: '0.9rem'
                }}>
                  {optionData.reason || 'IV از قیمت فعلی قابل محاسبه نبود؛ حساسیت‌های فعلی نمایش داده نمی‌شوند.'}
                </div>
              )}

              <div className="greeks-section">
                <h4>پارامترهای یونانی (Greeks)</h4>
                {optionGreeks ? (
                  <div className="greeks-grid">
                    <div className="greek-card">
                      <div className="greek-label">Delta</div>
                      <div className="greek-value">{optionGreeks.delta}</div>
                    </div>
                    <div className="greek-card">
                      <div className="greek-label">Gamma</div>
                      <div className="greek-value">{optionGreeks.gamma}</div>
                    </div>
                    <div className="greek-card">
                      <div className="greek-label">Vega</div>
                      <div className="greek-value">{optionGreeks.vega}</div>
                    </div>
                    <div className="greek-card">
                      <div className="greek-label">Theta</div>
                      <div className="greek-value">{optionGreeks.theta}</div>
                    </div>
                    <div className="greek-card">
                      <div className="greek-label">Rho</div>
                      <div className="greek-value">{optionGreeks.rho}</div>
                    </div>
                    <div className="greek-card">
                      <div className="greek-label">IV</div>
                      <div className="greek-value">{optionData.ivSolved ? `${optionData.ivTodayPercent}%` : 'حل نشد'}</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#ff9800', padding: '15px', textAlign: 'center' }}>
                    ⚠️ پارامترهای Greeks محاسبه نشده‌اند (IV حل نشد یا داده نامعتبر)
                  </div>
                )}
              </div>

              <div className="predictions-section">
                <h4>قیمت مبنا با نوسان ثابت مشاهده قبلی</h4>
                <p>قیمت سهم پایه و زمان باقی‌ماندهٔ امروز، با IV مشاهده قبلی در فرمول کامل بلک–شولز محاسبه می‌شود. این مقایسه، پیش‌بینی قیمت آینده یا اثبات حباب نیست.</p>
                {optionData.predictions.referencePrice !== null ? <>
                  <div className="option-value-card time"><div className="option-value-label">قیمت مبنای مدل</div><div className="option-value-value">{optionData.predictions.referencePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })} ریال</div></div>
                  {optionData.predictions.referencePrice > 0 && <p>اختلاف قیمت فعلی با مبنا: {((currentPrice / optionData.predictions.referencePrice - 1) * 100).toFixed(2)}٪</p>}
                  <p>تغییر سهم پایه: {optionData.predictions.actualDeltaS?.toLocaleString()} ریال · فاصله مشاهده قبلی: {optionData.elapsedDays} روز تقویمی</p>
                  <p>تغییر IV: {optionData.deltaIVPercent === 'N/A' ? 'قابل محاسبه نیست' : `${optionData.deltaIVPercent} واحد درصد`}</p>
                </> : <p role="status">{optionData.referenceReason}</p>}
                <p>دلتا: تغییر قیمت به ازای یک ریال سهم پایه · گاما: تغییر دلتا به ازای یک ریال · وگا و رو: ریال به ازای یک واحد درصد · تتا: ریال در یک روز تقویمی.</p>
              </div>
            </>
          )}
        </div>
      )}
      {resultData && resultData.rows.length > 0 && (
        <div className="results-section">
                    <div className="total-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              📊 مجموع تعداد دارایی (فیلتر {resultData.filter}): {resultData.totalRemainingQty.toLocaleString()} واحد
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={selectPositiveSteps}
                style={{ background: '#28a745', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                ✅ انتخاب همه سودده‌ها
              </button>
              <button
                onClick={openBulkModal}
                disabled={selectedSteps.length === 0}
                style={{ background: selectedSteps.length === 0 ? '#ccc' : '#007bff', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: selectedSteps.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
              >
                🛒 فروش گروهی ({selectedSteps.length} پله)
              </button>
              {(selectedSymbol.startsWith('ض') || selectedSymbol.startsWith('ط')) && resultData?.totalRemainingQty > 0 && (
                <>
                  <button
                    onClick={openCashSettleModal}
                    style={{ background: '#17a2b8', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    💰 تسویه نقدی
                  </button>
                  <button
                    onClick={openPhysicalSettleModalSymbolLevel}
                    style={{ background: '#fd7e14', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    📦 تسویه فیزیکی
                  </button>
                </>
              )}
            </div>
          </div>
          
          <div className="table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>انتخاب</th>
                  <th>نماد</th>
                  <th>پله</th>
                  <th>وضعیت</th>
                  <th>تعداد روز</th>
                  <th>تعداد مانده</th>
                  <th>سرمایه درگیر</th>
                  <th>سود/زیان بازار</th>
                  <th>هزینه فرصت</th>
                  <th>عملکرد خالص</th>
                  <th>درصد عملکرد</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {resultData.rows.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      {row.status === '🟢 باز' && (
                        <input
                          type="checkbox"
                          checked={selectedSteps.includes(row.step)}
                          onChange={() => toggleStepSelection(row.step)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                      )}
                    </td>
                    <td style={{ fontWeight: 'bold', color: '#1976d2' }}>{row.symbolName || selectedSymbol}</td>
                    <td>{row.step}</td>
                    <td>{row.status}</td>

                    <td>{row.daysHeld}</td>
                    <td>{row.remainingQty}</td>
                    <td>{row.investedFormatted}</td>
                    <td className={row.marketPnl < 0 ? 'negative' : 'positive'}>{row.marketPnlFormatted}</td>
                    <td className="opp-cost">{row.oppCostFormatted}</td>
                    <td className={row.netPnl < 0 ? 'negative' : 'positive'}>{row.netPnlFormatted}</td>
                    <td className={row.percentValue < 0 ? 'negative' : 'positive'}>{row.percentFormatted}</td>
                                        <td>
                      {row.status === '🟢 باز' && (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          {row.marketType === 'Option' && transactions.some(t => t.Step === row.step && t.Type === 'short_sell') ? (
                            <>
                              <button 
                                className="buy-close-btn"
                                onClick={() => openSellModal(row)} // باز کردن مودال برای خرید و تسویه نوسان پریمیوم
                                disabled={sellLoading}
                                style={{ background: '#28a745', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}
                              >
                                {sellLoading ? '⏳...' : '🛒 تسویه نوسان'}
                              </button>
                              <button 
                                className="physical-settle-btn"
                                onClick={() => openPhysicalSettleModal(row)}
                                disabled={sellLoading}
                                style={{ background: '#ff9800', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}
                              >
                                📦 تسویه فیزیکی
                              </button>
                            </>
                          ) : (
                            <button 
                              className="sell-row-btn"
                              onClick={() => openSellModal(row)}
                              disabled={sellLoading}
                            >
                              {sellLoading ? '⏳...' : '💰 فروش'}
                            </button>
                          )}
                        </div>
                      )}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="summary-cards">
            <div className="card">
              <div className="card-label">کل سرمایه درگیر</div>
              <div className="card-value">{Math.round(summary.totalInvested).toLocaleString()} ریال</div>
            </div>
            <div className="card">
              <div className="card-label">سود بازار</div>
              <div className="card-value">{Math.round(summary.totalMarketPnl).toLocaleString()} ریال</div>
            </div>
            <div className="card">
              <div className="card-label">هزینه فرصت</div>
              <div className="card-value">{Math.round(summary.totalOppCost).toLocaleString()} ریال</div>
            </div>
            <div className="card">
              <div className="card-label">عملکرد خالص</div>
              <div className={`card-value ${summary.grandNet < 0 ? 'negative' : 'positive'}`}>
                {Math.round(summary.grandNet).toLocaleString()} ریال
                {summary.totalInvested > 0 && (
                  <span className="delta">({formatPercent((summary.grandNet / summary.totalInvested) * 100)})</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="bank-comparison">
            <h3>📝 گزارش تحلیلی و مقایسه با بانک برای نماد {summary.selectedSymbol}</h3>
            {summary.totalInvested > 0 ? (
              <>
                <div className="comparison-text">
                  <p><strong>خلاصه وضعیت سرمایه‌گذاری شما:</strong></p>
                  <p>- میانگین سرمایه درگیر شما در این نماد: <strong>{Math.round(summary.totalInvested).toLocaleString()} ریال</strong></p>
                  <p>- سود/زیان شما در بازار بورس: <strong>{Math.round(summary.totalMarketPnl).toLocaleString()} ریال</strong> (معادل <strong>{formatPercent(getStockReturnPercent())}</strong>)</p>
                  <p>- اگر همین پول در بانک بود چقدر سود می‌داد؟ <strong>{Math.round(summary.totalOppCost).toLocaleString()} ریال</strong> (معادل <strong>{formatPercent(getBankReturnPercent())}</strong>)</p>
                </div>
                
                {summary.grandNet > 0 ? (
                  <div className="success-box">
                    🎉 <strong>نتیجه نهایی:</strong> سرمایه‌گذاری در این سهم عالی بوده است! شما نسبت به سپرده بانکی <strong>{Math.round(Math.abs(summary.grandNet)).toLocaleString()} ریال</strong> (معادل {Math.abs(getDiffPercent()).toFixed(2)}% از سرمایه) <strong>بیشتر</strong> سود کرده‌اید.
                  </div>
                ) : summary.grandNet < 0 ? (
                  <div className="error-box">
                    ⚠️ <strong>نتیجه نهایی:</strong> این سرمایه‌گذاری بهینه نبوده است. شما نسبت به سپرده بانکی <strong>{Math.round(Math.abs(summary.grandNet)).toLocaleString()} ریال</strong> (معادل {Math.abs(getDiffPercent()).toFixed(2)}% از سرمایه) <strong>عقب‌تر</strong> هستید. نگهداری این پول در بانک منطقی‌تر بود.
                  </div>
                ) : (
                  <div className="info-box">
                    ⚖️ <strong>نتیجه نهایی:</strong> سود شما در این سهم دقیقاً معادل سود بانکی بوده است. (عملکرد خنثی)
                  </div>
                )}
              </>
            ) : (
              <div className="info-box">هنوز سرمایه درگیری برای این نماد محاسبه نشده است.</div>
            )}
          </div>
          
          <div className="analysis-links">
            <h3>🔍 ابزارهای تحلیل پیشرفته برای نماد {summary.selectedSymbol}</h3>
            <p>برای مشاهده جزئیات بیشتر مانند <strong>قدرت خریدار/فروشنده حقیقی</strong>، <strong>ورود و خروج پول</strong> و <strong>رفتار سهامداران عمده</strong>، از لینک‌های زیر استفاده کنید:</p>
            <div className="links-container">
              <a href={`http://www.fipiran.com/Symbol?symbol=${summary.selectedSymbol}`} target="_blank" rel="noopener noreferrer" className="link-btn">
                🔗 صفحه رسمی نماد در فیپیران (بورس)
              </a>
              <a href={`https://www.google.com/search?q=سایت+بورس+tsetmc+نماد+${summary.selectedSymbol}`} target="_blank" rel="noopener noreferrer" className="link-btn">
                🔗 جستجوی صفحه سهم در سایت TSETMC
              </a>
              <a href={`https://www.sahamyab.com/stock/${summary.selectedSymbol}`} target="_blank" rel="noopener noreferrer" className="link-btn">
                🔗 اطلاعات، حقیقی/حقوقی و نظرات (سهام‌یاب)
              </a>
            </div>
          </div>
        </div>
      )}
      
      {resultData && resultData.rows.length === 0 && (
        <div className="info-box">هیچ داده‌ای با فیلتر انتخاب شده یافت نشد.</div>
      )}

            {/* مودال فروش گروهی */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => !sellLoading && setShowBulkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🛒 تایید فروش گروهی</h3>
              <button className="modal-close" onClick={() => !sellLoading && setShowBulkModal(false)} disabled={sellLoading}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                {(() => {
                  const rowsToSell = resultData.rows.filter(r => selectedSteps.includes(r.step) && r.status === '🟢 باز');
                  const totalQty = rowsToSell.reduce((sum, r) => sum + r.remainingQty, 0);
                  
                  // محاسبه تخمینی سود بر اساس قیمت وارد شده در کادر فروش
                  const totalNetPnl = rowsToSell.reduce((sum, r) => {
                    const priceDiff = bulkSellPrice - currentPrice;
                    // در فروش فزاینده وقتی قیمت بالا میره ضرر میکنیم، پس تفاوت معکوس است
                    const valueDelta = r.isShortSell ? (-priceDiff * r.remainingQty) : (priceDiff * r.remainingQty);
                    return sum + r.netPnl + valueDelta;
                  }, 0);

                  return (
                    <>
                      <p><strong>نماد:</strong> {selectedSymbol}</p>
                      <p><strong>تعداد پله‌های انتخابی:</strong> {rowsToSell.length} پله</p>
                      <p><strong>جمع کل تعداد برای فروش:</strong> {totalQty.toLocaleString()} سهم</p>
                      <p><strong>تعداد قرارداد آپشن:</strong> {(totalQty / optionContractSize).toLocaleString()} قرارداد</p>
                      <p><strong>تخمین عملکرد خالص با قیمت جدید:</strong> <span className={totalNetPnl < 0 ? 'negative' : 'positive'}>{totalNetPnl.toLocaleString()} ریال</span></p>
                    </>
                  );
                })()}
              </div>
              
              <div className="modal-form">
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label>تاریخ فروش:</label>
                  <input 
                    type="date" 
                    value={bulkSellDate} 
                    onChange={(e) => setBulkSellDate(e.target.value)}
                    disabled={sellLoading}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label>اندازه قرارداد (فقط جهت محاسبه آپشن):</label>
                  <input 
                    type="number" 
                    value={optionContractSize} 
                    onChange={(e) => setOptionContractSize(parseInt(e.target.value) || 1)}
                    min={1}
                    disabled={sellLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label>قیمت فروش برای همه پله‌ها (ریال):</label>
                  <input 
                    type="number" 
                    value={bulkSellPrice}
                    onChange={(e) => setBulkSellPrice(parseFloat(e.target.value))}
                    step={100}
                    min={0}
                    disabled={sellLoading}
                  />
                  <small style={{display:'block', marginTop:'5px'}}>این قیمت برای تمامی پله‌های انتخاب شده اعمال می‌شود.</small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-modal-btn" onClick={() => !sellLoading && setShowBulkModal(false)} disabled={sellLoading}>
                انصراف
              </button>
              <button className="confirm-sell-btn" onClick={handleBulkSell} disabled={sellLoading}>
                {sellLoading ? '⏳ در حال ثبت...' : '✅ تایید و فروش همه'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* مودال فروش */}
      {showSellModal && selectedStepForSell && (
        <div className="modal-overlay" onClick={() => !sellLoading && setShowSellModal(false)}>
          {/* محتوای داخل مدال فروش شما در اینجا قرار میگیرد (بدون تغییر نسبت به قبل) */}
        </div>
      )}

      {/* مدال تسویه فیزیکی */}
      {showPhysicalModal && selectedPhysicalStep && (
        <div className="modal-overlay" onClick={() => !sellLoading && setShowPhysicalModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📦 ماشین حساب و تسویه فیزیکی فروش فزاینده</h3>
              <button className="modal-close" onClick={() => !sellLoading && setShowPhysicalModal(false)} disabled={sellLoading}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <p><strong>نماد آپشن:</strong> {selectedSymbol}</p>
                <p><strong>نوع آپشن:</strong> {physicalType === 'put' ? 'اختیار فروش (Put)' : 'اختیار خرید (Call)'}</p>
                <p><strong>نماد پایه تخصیص داده شده:</strong> {baseSymbol}</p>
                <p><strong>تعداد موقعیت‌های باز:</strong> {selectedPhysicalStep.remainingQty.toLocaleString()}</p>
                <p><strong>وجه تضمین اولیه درگیر شده:</strong> {selectedPhysicalStep.invested.toLocaleString()} ریال</p>
              </div>

              <div className="modal-form">
                <div className="form-group">
                  <label>اندازه قرارداد:</label>
                  <input type="number" value={contractSizeVal} onChange={(e) => setContractSizeVal(parseInt(e.target.value) || 1)} />
                </div>
                <div className="form-group">
                  <label>قیمت اعمال (ریال):</label>
                  <input type="number" value={strikePrice} onChange={(e) => setStrikePrice(parseFloat(e.target.value) || 0)} />
                </div>

                                {/* استایل رنگ متن اصلاح شد تا خوانا باشد */}
                <div className="modal-preview" style={{ background: '#f5f5f5', color: '#333', padding: '15px', borderRadius: '8px', marginTop: '15px' }}>
                  <p><strong>📝 محاسبات نهایی تسویه فیزیکی:</strong></p>
                  {physicalType === 'put' ? (
                    <>
                      <p>مبلغ خرید سهام پایه: {(selectedPhysicalStep.remainingQty * strikePrice).toLocaleString()} ریال</p>
                      <p>منهای وجه تضمین آزاد شده: {selectedPhysicalStep.invested.toLocaleString()} ریال</p>
                      <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>الباقی مبلغ واریز نقدی مورد نیاز: {customSettleAmount.toLocaleString()} ریال</p>
                      <p style={{ color: '#28a745' }}>تعداد سهم پایه {baseSymbol} که به سبد سهام شما اضافه خواهد شد: {selectedPhysicalStep.remainingQty.toLocaleString()} سهم</p>
                    </>
                  ) : (
                    <>
                      <p style={{ color: '#333' }}>تعداد سهم پایه {baseSymbol} جهت واگذاری: {selectedPhysicalStep.remainingQty.toLocaleString()} سهم</p>
                      <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>سهم از سبد کسر شده و وجه تضمین اولیه به حساب شما بازگردانده می‌شود.</p>
                    </>
                  )}
                </div>

              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-modal-btn" onClick={() => !sellLoading && setShowPhysicalModal(false)} disabled={sellLoading}>
                انصراف
              </button>
              <button className="confirm-sell-btn" onClick={handlePhysicalSettleSubmit} disabled={sellLoading}>
                {sellLoading ? '⏳ در حال ثبت...' : '✅ تایید و تسویه نهایی'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ مودال تسویه نقدی ============ */}
      {showCashSettleModal && (
        <div className="modal-overlay" onClick={() => !sellLoading && setShowCashSettleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💰 تسویه نقدی</h3>
              <button className="modal-close" onClick={() => !sellLoading && setShowCashSettleModal(false)} disabled={sellLoading}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <p><strong>نماد آپشن:</strong> {selectedSymbol}</p>
                <p><strong>نوع:</strong> {selectedSymbol.startsWith('ض') ? 'اختیار خرید (Call)' : 'اختیار فروش (Put)'}</p>
                {(() => {
                  const stats = getOpenOptionStats();
                  const totalContracts = Math.floor(stats.totalShares / 1000);
                  return (
                    <>
                      <p><strong>کل موقعیت باز:</strong> {stats.totalShares.toLocaleString()} سهم ({totalContracts} قرارداد)</p>
                    </>
                  );
                })()}
              </div>

              <div className="modal-form">
                <div className="form-group">
                  <label>اندازه قرارداد:</label>
                  <input type="number" value={cashSettleContractSize} onChange={(e) => setCashSettleContractSize(parseInt(e.target.value) || 1)} />
                </div>
                <div className="form-group">
                  <label>تعداد قرارداد برای تسویه:</label>
                  <input type="number" value={cashSettleContracts} onChange={(e) => setCashSettleContracts(parseInt(e.target.value) || 0)} min={0} />
                  <label style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={cashSettleSelectAll} onChange={(e) => {
                      setCashSettleSelectAll(e.target.checked);
                      if (e.target.checked) {
                        const stats = getOpenOptionStats();
                        setCashSettleContracts(Math.floor(stats.totalShares / 1000));
                      }
                    }} />
                    انتخاب همه قراردادهای باز
                  </label>
                </div>
                <div className="form-group">
                  <label>قیمت اعمال (Strike):</label>
                  <input type="number" value={cashSettleStrikePrice} onChange={(e) => setCashSettleStrikePrice(parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label>قیمت پایانی نماد پایه در سررسید:</label>
                  <input type="number" value={cashSettleFinalPrice} onChange={(e) => setCashSettleFinalPrice(parseFloat(e.target.value) || 0)} />
                </div>

                {/* پیش‌نمایش محاسبات */}
                {(() => {
                  const contractSize = cashSettleContractSize || 1000;
                  const totalShares = cashSettleContracts * contractSize;
                  const isCall = selectedSymbol.startsWith('ض');
                  const priceDiff = isCall
                    ? Math.max(0, cashSettleFinalPrice - cashSettleStrikePrice)
                    : Math.max(0, cashSettleStrikePrice - cashSettleFinalPrice);
                  const gross = priceDiff * totalShares;
                  const cashSettleComm = getSettingValue('option_cash_settle_comm', 0.00103);
                  const commission = gross * cashSettleComm;
                  const net = gross - commission;

                  return (
                    <div className="modal-preview" style={{ background: '#1a1a2e', color: '#e8e8e8', padding: '15px', borderRadius: '8px', marginTop: '15px' }}>
                      <p><strong>📝 محاسبات تسویه نقدی:</strong></p>
                      <p>تعداد سهم: {totalShares.toLocaleString()}</p>
                      <p>تفاوت قیمت: {priceDiff.toLocaleString()} ریال</p>
                      <p>مبلغ ناخالص: {gross.toLocaleString()} ریال</p>
                      <p>کمیسیون ({(cashSettleComm * 100).toFixed(2)}%): {commission.toLocaleString()} ریال</p>
                      <p style={{ color: '#4caf50', fontSize: '1.2rem', fontWeight: 'bold' }}>خالص دریافتی: {net.toLocaleString()} ریال</p>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-modal-btn" onClick={() => !sellLoading && setShowCashSettleModal(false)} disabled={sellLoading}>
                انصراف
              </button>
              <button className="confirm-sell-btn" onClick={handleCashSettleSubmit} disabled={sellLoading}>
                {sellLoading ? '⏳ در حال ثبت...' : '✅ تایید تسویه نقدی'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ مودال تسویه فیزیکی ============ */}
      {showPhysicalSettleModal && (
        <div className="modal-overlay" onClick={() => !sellLoading && setShowPhysicalSettleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📦 تسویه فیزیکی</h3>
              <button className="modal-close" onClick={() => !sellLoading && setShowPhysicalSettleModal(false)} disabled={sellLoading}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <p><strong>نماد آپشن:</strong> {selectedSymbol}</p>
                <p><strong>نماد پایه:</strong> {getBaseSymbolForOption(selectedSymbol)}</p>
                {(() => {
                  const stats = getOpenOptionStats();
                  const totalContracts = Math.floor(stats.totalShares / 1000);
                  return (
                    <>
                      <p><strong>کل موقعیت باز:</strong> {stats.totalShares.toLocaleString()} سهم ({totalContracts} قرارداد)</p>
                      <p><strong>میانگین سرمایه درگیر هر سهم:</strong> {Math.round(stats.avgCostPerShare).toLocaleString()} ریال</p>
                      <p><strong>هزینه فرصت کل:</strong> {Math.round(stats.totalOppCost).toLocaleString()} ریال</p>
                    </>
                  );
                })()}
              </div>

              <div className="modal-form">
                <div className="form-group">
                  <label>اندازه قرارداد:</label>
                  <input type="number" value={physicalSettleContractSize} onChange={(e) => setPhysicalSettleContractSize(parseInt(e.target.value) || 1)} />
                </div>
                <div className="form-group">
                  <label>تعداد قرارداد برای تسویه:</label>
                  <input type="number" value={physicalSettleContracts} onChange={(e) => setPhysicalSettleContracts(parseInt(e.target.value) || 0)} min={0} />
                  <label style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={physicalSettleSelectAll} onChange={(e) => {
                      setPhysicalSettleSelectAll(e.target.checked);
                      if (e.target.checked) {
                        const stats = getOpenOptionStats();
                        setPhysicalSettleContracts(Math.floor(stats.totalShares / 1000));
                      }
                    }} />
                    انتخاب همه قراردادهای باز
                  </label>
                </div>
                <div className="form-group">
                  <label>قیمت اعمال (Strike):</label>
                  <input type="number" value={physicalSettleStrikePrice} onChange={(e) => setPhysicalSettleStrikePrice(parseFloat(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label>قیمت پایانی نماد پایه در سررسید:</label>
                  <input type="number" value={physicalSettleFinalPrice} onChange={(e) => setPhysicalSettleFinalPrice(parseFloat(e.target.value) || 0)} />
                </div>

                {/* پیش‌نمایش محاسبات */}
                {(() => {
                  const contractSize = physicalSettleContractSize || 1000;
                  const totalShares = physicalSettleContracts * contractSize;
                  const settlementAmount = totalShares * physicalSettleStrikePrice;
                  const physicalCommRate = getSettingValue('option_physical_settle_comm', 0.0055);
                  const physicalCommission = settlementAmount * physicalCommRate;
                  const stats = getOpenOptionStats();
                  const premiumAndOppCost = totalShares > 0 && stats.totalShares > 0
                    ? (stats.avgCostPerShare * totalShares) + (stats.totalOppCost * (totalShares / stats.totalShares))
                    : 0;
                  const totalPayable = settlementAmount + physicalCommission + premiumAndOppCost;
                  const costPerShare = totalShares > 0 ? totalPayable / totalShares : 0;

                  return (
                    <div className="modal-preview" style={{ background: '#1a1a2e', color: '#e8e8e8', padding: '15px', borderRadius: '8px', marginTop: '15px' }}>
                      <p><strong>📝 محاسبات تسویه فیزیکی:</strong></p>
                      <p>مبلغ تسویه (قیمت اعمال × تعداد سهم): {settlementAmount.toLocaleString()} ریال</p>
                      <p>کمیسیون تسویه فیزیکی ({(physicalCommRate * 100).toFixed(2)}%): {physicalCommission.toLocaleString()} ریال</p>
                      <p>پریمیوم پرداختی + هزینه فرصت: {Math.round(premiumAndOppCost).toLocaleString()} ریال</p>
                      <p style={{ borderTop: '1px solid #444', paddingTop: '8px', fontSize: '1.1rem' }}>
                        <strong>مبلغ پرداختی نهایی:</strong> {Math.round(totalPayable).toLocaleString()} ریال
                      </p>
                      <p style={{ color: '#4caf50', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        قیمت تمام‌شده هر سهم: {costPerShare.toFixed(2)} ریال
                      </p>
                      <p>تعداد سهم دریافتی: {totalShares.toLocaleString()} سهم</p>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-modal-btn" onClick={() => !sellLoading && setShowPhysicalSettleModal(false)} disabled={sellLoading}>
                انصراف
              </button>
              <button className="confirm-sell-btn" onClick={handlePhysicalSettleSubmitSymbolLevel} disabled={sellLoading}>
                {sellLoading ? '⏳ در حال ثبت...' : '✅ تایید تسویه فیزیکی'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


export default StockAnalysis;
